import { ArrowDownIcon, ArrowUpIcon } from "@phosphor-icons/react";

import type { SafetyProfileHeadlineMetric } from "@miter/types/contracts";
import { cn } from "ui2";

import { formatRate, headlineDelta } from "safety/components/profile/format";
import { MetricInfo } from "safety/components/profile/MetricInfo";

const HEADLINE_LABELS: Record<SafetyProfileHeadlineMetric["key"], string> = {
  emr: "EMR",
  trir: "TRIR",
  dart: "DART",
};

const HEADLINE_INFO: Record<SafetyProfileHeadlineMetric["key"], string> = {
  emr: "Experience modification rate. The multiplier a workers' comp carrier applies to a contractor's premium. 1.00 is the industry average, and lower means fewer and cheaper claims than a comparable contractor.",
  trir: "Total recordable incident rate. Recordable injuries times 200,000, divided by hours worked over the last 12 months.",
  dart: "Days away, restricted, or transferred. The share of injuries serious enough to keep someone off their normal work, times 200,000, divided by hours worked over the last 12 months.",
};

const Delta = ({ metric }: { metric: SafetyProfileHeadlineMetric }): React.JSX.Element | null => {
  if (metric.value === null || metric.benchmark === null) return null;

  const difference = headlineDelta(metric.value, metric.benchmark);
  if (difference === 0) {
    return <span className="tw:text-sm tw:text-midnight/50">At benchmark</span>;
  }

  const beatsBenchmark = difference > 0;
  const DeltaIcon = beatsBenchmark ? ArrowDownIcon : ArrowUpIcon;
  const delta = formatRate(Math.abs(difference));

  return (
    <span
      className={
        beatsBenchmark
          ? "tw:inline-flex tw:items-center tw:gap-0.5 tw:text-sm tw:font-medium tw:text-primary-purple"
          : "tw:inline-flex tw:items-center tw:gap-0.5 tw:text-sm tw:font-medium tw:text-secondary-clay"
      }
    >
      <DeltaIcon weight="bold" className="tw:size-3" />
      {beatsBenchmark ? `${delta} under` : `${delta} over`}
    </span>
  );
};

type HeadlineMetricProps = {
  metric: SafetyProfileHeadlineMetric;
};

const HeadlineMetric = ({ metric }: HeadlineMetricProps): React.JSX.Element => {
  const label = HEADLINE_LABELS[metric.key];
  const benchmarkLabel = metric.benchmark === null ? null : `Benchmark ${formatRate(metric.benchmark)}`;

  return (
    <div className="tw:px-5 tw:py-4">
      <div className="tw:flex tw:items-center tw:gap-1.5">
        <span className="tw:text-xs tw:font-medium tw:uppercase tw:tracking-wide tw:text-midnight/50">
          {label}
        </span>
        <MetricInfo text={HEADLINE_INFO[metric.key]} label={label} />
      </div>
      <div className="tw:mt-1 tw:flex tw:flex-wrap tw:items-baseline tw:gap-x-2 tw:gap-y-1">
        <span className="tw:font-denim tw:text-4xl tw:leading-none tw:tabular-nums tw:text-midnight">
          {metric.value === null ? "—" : formatRate(metric.value)}
        </span>
        <Delta metric={metric} />
      </div>
      {benchmarkLabel && <div className="tw:mt-2 tw:text-xs tw:text-midnight/60">{benchmarkLabel}</div>}
    </div>
  );
};

type HeadlineMetricsProps = {
  metrics: SafetyProfileHeadlineMetric[];
};

// Companies can hide individual rate metrics, so the band is 1-3 columns.
const GRID_COLS_BY_COUNT: Record<number, string> = {
  1: "tw:sm:grid-cols-1",
  2: "tw:sm:grid-cols-2",
  3: "tw:sm:grid-cols-3",
};

export const HeadlineMetrics = ({ metrics }: HeadlineMetricsProps): React.JSX.Element => (
  <div
    className={cn(
      "tw:grid tw:grid-cols-1 tw:divide-y tw:divide-stroke tw:border-t tw:border-stroke tw:bg-white tw:sm:divide-x tw:sm:divide-y-0",
      GRID_COLS_BY_COUNT[metrics.length] ?? "tw:sm:grid-cols-3"
    )}
  >
    {metrics.map((metric) => (
      <HeadlineMetric key={metric.key} metric={metric} />
    ))}
  </div>
);
